<template>
    <page-template page-id="SetEmail" ref="super">

        <document-title :title="$tb('pages.setEmail.documentTitle')"></document-title>

        <template v-slot:before v-if="isTitlebarActive">
            <titlebar :text="$tb('pages.setEmail.header', {0: $store.state.appName})"></titlebar>
        </template>

        <div class="ui column">

            <brand-header :text="$t('pages.setEmail.title')"
                          :has-extension="hasHeaderExtension"
                          :has-logo="hasBrandLogo">
            </brand-header>

            <p v-html="$t( 'pages.setEmail.subheader' )"></p>

            <messages id="Notifications" ref="postman"></messages>

            <validated-field :show-errors-on="showErrors" :has-any-errors="$v.newEmail.$invalid"
                             id="email-errors"
                             :validation-rules="[
                             {value: $v.newEmail.required, msg: $t('common.validation.username.emailEmpty')},
                             {value: $v.newEmail.isValidEmail, showOn: $v.newEmail.required, msg: $t('common.validation.username.emailFormat')}]">
                <labeled-input-field id="email" v-model="$v.newEmail.$model"
                                     autocomplete="email"
                                     :label="$t('common.label.newEmail')"
                                     @click.right.prevent @copy.prevent @paste.prevent
                                     @enter-pressed.prevent="onConfirm"
                                     @input="onEmailChange"
                                     aria-error-element-id="email-errors notifications"
                                     :mandatory="true"
                                     :invalid="$v.newEmail.isValidEmail && showErrors"></labeled-input-field>
            </validated-field>

            <validated-field :show-errors-on="showErrors" :has-any-errors="$v.confirmEmail.$invalid"
                             id="confirmEmail-errors"
                             :validation-rules="[
                               {value: $v.confirmEmail.required, msg: $t('common.validation.username.confirmEmailEmpty')},
                               {value: $v.confirmEmail.isValidEmail, showOn: $v.confirmEmail.required, msg: $t('common.validation.username.emailFormat')},
                               {value: $v.confirmEmail.sameAsEmail, showOn: $v.confirmEmail.isValidEmail, msg: $t('common.validation.username.mismatchedEmail')}
                               ]">
              <labeled-input-field id="confirmEmail" v-model="$v.confirmEmail.$model"

                                   :label="$t('common.label.confirmEmail')"
                                   @input="onConfirmEmailChange"
                                   @click.right.prevent @copy.prevent @paste.prevent
                                   @enter-pressed.prevent="onConfirm"
                                   aria-error-element-id="confirmEmail-errors notifications"
                                   :mandatory="true"
                                   :invalid="$v.confirmEmail.$invalid && showErrors"></labeled-input-field>
            </validated-field>

            <validated-field id="password-errors"
                             :show-errors-on="showErrors" :has-any-errors="$v.password.$invalid"
                             :validation-rules="[{value: $v.password.required, msg: this.$t('common.validation.field.empty')}]">
                <password-input-field id="password" v-model="$v.password.$model"
                                     autocomplete="current-password"
                                     :label="$t('common.label.password')"
                                     :invalid="$v.password.$invalid && showErrors"
                                     :mandatory="true"
                                     aria-error-element-id="password-errors server-errors"
                                     @enter-pressed.prevent="onConfirm"></password-input-field>
            </validated-field>

            <div class="field">
                <button id="confirm" type="submit" class="ui fluid primary button" @click.prevent="onConfirm">
                    {{$t('common.button.continue')}}
                </button>
            </div>
            <div class="field">
              <button id="back" type="button" v-text="$t('common.button.cancel')"
                      class="ui fluid basic button" @click="goBack"></button>
            </div>
        </div>
    </page-template>
</template>

<script>
import PageTemplate from './template/PageTemplate.vue'
import Titlebar from '../components/Titlebar.vue'
import BrandHeader from '../components/BrandHeader.vue'
import LabeledInputField from '../components/LabeledInputField.vue'
import PasswordInputField from '../components/LabeledPasswordInputField.vue'
import Messages from '../components/Messages.vue'
import ValidatedField from '../components/ValidatedField.vue'
import { mixin } from '../js/ciam-mixin.js'
import { brand } from '../js/brand.js'
import * as validate from '../js/validation.js'
import { focusOn } from '../js/helpers'
import { required, sameAs } from 'vuelidate/lib/validators'

export default {
  components: {
    PageTemplate,
    Titlebar,
    BrandHeader,
    Messages,
    LabeledInputField,
    ValidatedField,
    PasswordInputField
  },
  mixins: [mixin, brand],
  props: {
    unconfirmedUsername: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      username: sessionStorage.getItem('storedusername') || this.$store.state.username,
      uid: this.$store.state.uid,
      newEmail: '',
      confirmEmail: '',
      password: '',
      operation: 'pwdreset',
      showErrors: false,
      matchedEmail: false,
      validEmail: false,
      validConfirmEmail: false
    }
  },
  validations () {
    return {
      newEmail: {
        required,
        isValidEmail () {
          return this.validEmail
        }
      },
      confirmEmail: {
        required,
        isValidEmail () {
          return this.validConfirmEmail
        },
        sameAsEmail: sameAs('newEmail')
      },
      password: { required }
    }
  },
  methods: {
    onConfirm: function () {
      this.$refs.postman.clear()
      this.showErrors = false

      this.newEmail = this.newEmail.replace(/＠/g, '@')
      this.confirmEmail = this.confirmEmail.replace(/＠/g, '@')

      if (!this.isFormValid()) return

      this.$refs.super.startLoading()

      const payload = this.$api.payloads.buildSetEmailPayload(this.username, this.newEmail, this.password)
      this.$api.login.setEmail(payload, this.handleRoutingResponse, this.onFail)
    },
    isFormValid: function () {
      if (!this.$v.$anyDirty || this.$v.$anyError || this.$v.$invalid) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
        })

        return false
      }

      return true
    },
    onFail: function (response) {
      this.onAjaxError(response)
      focusOn('email')
      this.$refs.super.stopLoading()
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return
      this.showErrors = false
    },
    onEmailChange: function () {
      this.validEmail = !!this.newEmail && validate.validateEmail(this.newEmail.trim())
      $('#email').val(this.newEmail)
    },
    onConfirmEmailChange: function () {
      if (this.confirmEmail) {
        this.validConfirmEmail = !!this.confirmEmail && validate.validateEmail(this.confirmEmail.trim())
        if (this.validConfirmEmail) {
          this.matchedEmail = this.newEmail === this.confirmEmail
        }
        if (!this.matchedEmail) {
          this.showErrors = true
        }
        $('#confirmEmail').val(this.confirmEmail)
      } else {
        this.validConfirmEmail = false
      }
    }
  },
  mounted: function () {
    const initialUsername = this.unconfirmedUsername || this.$store.state.unconfirmedUsername
    this.unconfirmedUsername = this.isEmail(initialUsername) ? initialUsername : ''
    if (this.unconfirmedUsername) {
      this.newEmail = this.unconfirmedUsername
      this.validEmail = validate.validateEmail(this.newEmail)
    }
    console.log(`Set email [uid=${this.uid}]`)
    $('#email').val(this.unconfirmedUsername)
    focusOn('newEmail')
  },
  watch: {
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    },
    password: {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.showErrors = false
          $('#grp_password').siblings('.ui.basic.red').remove()
        }
      }
    }
  }
}
</script>

<style scoped>
</style>
